import {
  telHref, formatPhone, directionsUrl, buildWhatsAppUrl, whatsappMessage,
  type Branch, type HomepageConfig, type SiteSettings,
} from '@cng/shared';
import { Link } from 'react-router-dom';
import { PinIcon, PhoneIcon, ClockIcon, MapIcon } from '@/components/common/Icons';
import { AccentedLines } from '@/components/common/AccentedTitle';

interface LocationsSectionProps {
  config: HomepageConfig['locations'];
  branches: Branch[];
  settings: SiteSettings;
}

/** Branch card fields the API enriches at read time. */
type BranchWithState = Branch & {
  isOpenNow?: boolean;
  openLabel?: string;
  todayHours?: string;
  hoursSummary?: string[];
};

/**
 * The "Find Us" block that closes every page. Everything here — addresses,
 * hours, phone numbers, the open/closed badge — comes from the Branch
 * collection, so a third salon is an admin task rather than a code change.
 */
export function LocationsSection({ config, branches, settings }: LocationsSectionProps) {
  if (!config?.enabled || branches.length === 0) return null;

  return (
    <div className="cgl-wrap">
      <section id="locations" className="cgl-section" aria-labelledby="locations-title">
        <div className="cgl-header">
          {config.tag ? (
            <div className="cgl-tag">
              <span className="cgl-tag-bar" aria-hidden="true" />
              {config.tag}
            </div>
          ) : null}
          <AccentedLines
            as="h2"
            className="cgl-title"
            id="locations-title"
            text={config.title}
            accent={config.titleAccent}
          />
          {config.subtitle ? <p className="cgl-sub">{config.subtitle}</p> : null}
        </div>

        <div className="cgl-grid" data-branch-count={branches.length}>
          {(branches as BranchWithState[]).map((branch) => {
            const open = branch.isOpenNow;
            const waMessage = whatsappMessage(
              'branchBooking',
              { branch: branch.shortName || branch.name },
              settings.whatsappTemplates,
            );
            return (
              <article className="cgl-card" key={branch._id}>
                <div className="cgl-card-inner">
                  <div className="cgl-branch-badge">
                    <span className={`bdot${open ? ' green' : ''}`} aria-hidden="true" />
                    {open == null ? 'Call for hours' : open ? 'Open Now' : 'Closed Now'}
                  </div>

                  <h3 className="cgl-card-name">
                    {(branch.displayNameLines.length ? branch.displayNameLines : [branch.name]).map((line, i, arr) => (
                      <span key={line}>
                        {line}
                        {i < arr.length - 1 ? <br /> : null}
                      </span>
                    ))}
                    {branch.nameAccent ? <><br /><em>{branch.nameAccent}</em></> : null}
                  </h3>

                  {branch.areaTagline ? <p className="cgl-card-area">{branch.areaTagline}</p> : null}
                  <div className="cgl-divider" aria-hidden="true" />

                  <div className="cgl-info-list">
                    <div className="cgl-info-row">
                      <div className="cgl-info-icon" aria-hidden="true"><PinIcon /></div>
                      <div className="cgl-info-body">
                        <span className="cgl-info-label">Address</span>
                        <span className="cgl-info-val">
                          {branch.addressLines.map((line, i, arr) => (
                            <span key={line}>
                              {line}
                              {i < arr.length - 1 ? <br /> : null}
                            </span>
                          ))}
                        </span>
                      </div>
                    </div>

                    {branch.phone ? (
                      <div className="cgl-info-row">
                        <div className="cgl-info-icon" aria-hidden="true"><PhoneIcon /></div>
                        <div className="cgl-info-body">
                          <span className="cgl-info-label">Phone</span>
                          <span className="cgl-info-val">
                            <a href={telHref(branch.phone)} className="cgl-phone">
                              {branch.phoneDisplay || formatPhone(branch.phone)}
                            </a>
                          </span>
                        </div>
                      </div>
                    ) : null}

                    <div className="cgl-info-row">
                      <div className="cgl-info-icon" aria-hidden="true"><ClockIcon /></div>
                      <div className="cgl-info-body">
                        <span className="cgl-info-label">Hours</span>
                        <span className="cgl-info-val">
                          {(branch.hoursSummary ?? []).map((line, i, arr) => (
                            <span key={line}>
                              {line}
                              {i < arr.length - 1 ? <br /> : null}
                            </span>
                          ))}
                        </span>
                      </div>
                    </div>
                  </div>

                  <p><Link to={`/locations/${branch.slug}/`}>View branch details</Link></p>
                  <div className="cgl-btns">
                    {branch.phone ? (
                      <a href={telHref(branch.phone)} className="cgl-btn-call">
                        <PhoneIcon /> Call Now
                      </a>
                    ) : null}
                    {branch.whatsappNumber ? (
                      <a
                        href={buildWhatsAppUrl({ phone: branch.whatsappNumber, message: waMessage })}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="cgl-btn-wa"
                      >
                        WhatsApp
                      </a>
                    ) : null}
                    <a
                      href={directionsUrl(branch)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cgl-btn-map"
                    >
                      <MapIcon /> Directions
                    </a>
                  </div>

                  {branch.openLabel ? (
                    <div className="cgl-hours">
                      <span className="cgl-hours-dot" aria-hidden="true" />
                      {branch.openLabel}
                    </div>
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}
